import React from 'react';
import { useDispatch } from 'react-redux';
import { ErrorBoundary } from '@sentry/react';
import { Card, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { GridButton } from 'components/GridButton';
import { useMeasureListHeight } from 'containers/Col2/GridViews/util';
import { resetGrid } from 'actions/UI';
import List from 'components/List';
import * as ErrorFallback from 'components/ErrorFallback';
import { FallbackProps } from 'components/ErrorFallback/types';

const useStyles = makeStyles({
  container: {
    position: 'relative',
  },
  errorMessageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
});

function Fallback({ resetError, eventId }: FallbackProps) {
  const { t } = useTranslation('Sentry');
  const styles = useStyles();
  const dispatch = useDispatch();
  const { needsToMeasure, Measure, totalLines } = useMeasureListHeight();

  if (needsToMeasure) return Measure ?? null; // null to satisfy ErrorBoundary fallback prop type

  return (
    <div className={styles.container}>
      <List
        minLines={totalLines}
        maxLines={totalLines}
        items={Array(1000)}
        itemComponent={<GridButton disabled btnColor="grid_dark" />}
      />
      <div className={styles.errorMessageContainer}>
        <ErrorFallback.Container>
          <Card variant="outlined">
            <ErrorFallback.Title title={t('genericErrorBoundary.title2')} />
            <ErrorFallback.Actions
              resetError={resetError}
              eventId={eventId}
              onBeforeReloadComponent={() => dispatch(resetGrid())}
            />
          </Card>
        </ErrorFallback.Container>
      </div>
    </div>
  );
}

function GridErrorBoundary({ view, children }) {
  return (
    <ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('component', 'grid');
        scope.setTag('subcomponent', view);
      }}
      fallback={({ resetError, eventId }) => (
        <Fallback resetError={resetError} eventId={eventId} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export default GridErrorBoundary;
